import { array, boolean, discriminatedUnion, enum as enumFactory, iso, literal, null as nullFactory, number, strictObject, string, union } from "../../lazy-zod.js";
import { nonEmptyTrimmedStringFromUnknown } from "../protocol/shared.js";
import { agentProfileIdSchema, profileWorkingDirectorySchema, profileWorkspaceModeSchema } from "../protocol/profile.js";
import { runtimeTransportModeSchema } from "./controller-contract.js";
import { conversationExecutionStateSchema } from "./conversation-execution-state.js";
import { providerDispatchRuntimeContextSchema } from "./provider-runtime-context.js";
//#region src/schemas/daemon/control-plane.ts
const OPENMELD_DAEMON_CONTROL_PLANE_SCHEMA_VERSION = 1;
const openMeldDaemonControlPlaneCommandSchema = enumFactory([
	"status",
	"health",
	"history",
	"interrupt",
	"cancel",
	"conversation_execution_status",
	"request_agent_activity_sync",
	"sync_route_catalog",
	"set_local_agent_enabled",
	"set_profile_workspace",
	"service_update_readiness",
	"account_control_capabilities",
	"shutdown"
]);
const openMeldDaemonHealthStateSchema = enumFactory([
	"ok",
	"degraded",
	"down"
]);
const openMeldDaemonRuntimeHealthStateSchema = enumFactory([
	"healthy",
	"idle",
	"suspect",
	"down"
]);
const openMeldDaemonControlTargetSelectorSchema = discriminatedUnion("by", [strictObject({
	by: literal("dispatch"),
	dispatchId: string().min(1)
}), strictObject({
	by: literal("task"),
	taskId: string().min(1)
})]);
const openMeldDaemonInterruptRequestPayloadSchema = strictObject({
	runtimeCoordinatorExecutionId: string().min(1).optional(),
	target: openMeldDaemonControlTargetSelectorSchema,
	mode: enumFactory(["graceful", "force"]).optional(),
	reason: string().min(1).optional()
});
const openMeldDaemonCancelRequestPayloadSchema = strictObject({
	runtimeCoordinatorExecutionId: string().min(1).optional(),
	target: openMeldDaemonControlTargetSelectorSchema,
	reason: string().min(1).optional()
});
const openMeldDaemonShutdownRequestPayloadSchema = strictObject({
	accountAdmissionId: string().uuid().optional(),
	mode: enumFactory(["graceful", "force"]).default("graceful"),
	gracePeriodMs: number().int().nonnegative().default(5e3)
});
const openMeldDaemonServiceUpdateReadinessPayloadSchema = strictObject({
	acceptedWakeCount: number().int().nonnegative(),
	safeToSwitch: boolean(),
	unacknowledgedResultCount: number().int().nonnegative()
});
const openMeldDaemonConversationExecutionControllerLaunchStatusSchema = enumFactory([
	"missing",
	"disabled",
	"repair_required",
	"ready"
]);
const nullableAgentControllerRefSchema = union([string().min(1), nullFactory()]);
const nullableNonEmptyStringSchema = union([string().min(1), nullFactory()]);
const openMeldDaemonConversationExecutionStatusRequestPayloadSchema = strictObject({
	boundAgentControllerRef: nullableAgentControllerRefSchema,
	agentControllerConversationId: nullableNonEmptyStringSchema,
	agentControllerRef: string().min(1),
	spaceId: nonEmptyTrimmedStringFromUnknown,
	targetProfileId: agentProfileIdSchema,
	transportMode: runtimeTransportModeSchema,
	validate: boolean()
});
const openMeldDaemonSyncRouteCatalogRequestPayloadSchema = strictObject({ reason: string().min(1) });
const openMeldDaemonRequestAgentActivitySyncRequestPayloadSchema = strictObject({ reason: string().trim().min(1).max(128) });
const openMeldDaemonSetLocalAgentEnabledRequestPayloadSchema = strictObject({
	agentId: string().trim().min(1).max(256),
	enabled: boolean()
});
const localAgentInstallationOptionSchema = strictObject({
	installationId: string().trim().min(1).max(256),
	name: string().trim().min(1).max(256),
	details: string().trim().min(1).max(256),
	selected: boolean()
});
const openMeldDaemonManageLocalAgentInstallationRequestPayloadSchema = discriminatedUnion("operation", [strictObject({
	agentId: string().trim().min(1).max(256),
	operation: literal("list")
}), strictObject({
	agentId: string().trim().min(1).max(256),
	installationId: string().trim().min(1).max(256),
	operation: literal("select")
})]);
const openMeldDaemonSetProfileWorkspaceRequestPayloadSchema = strictObject({
	targetProfileId: agentProfileIdSchema,
	workspaceMode: profileWorkspaceModeSchema,
	workspacePath: profileWorkingDirectorySchema.optional()
}).superRefine((value, context) => {
	if (value.workspaceMode === "custom" && !value.workspacePath) context.addIssue({
		code: "custom",
		message: "workspacePath is required for a custom profile workspace",
		path: ["workspacePath"]
	});
	if (value.workspaceMode === "openmeld_managed" && value.workspacePath) context.addIssue({
		code: "custom",
		message: "workspacePath must be omitted for an OpenMeld-managed profile workspace",
		path: ["workspacePath"]
	});
});
const openMeldDaemonConversationExecutionRuntimeWorkspaceReadySchema = strictObject({
	configPath: string().min(1),
	configuredWorkspacePath: string().min(1),
	fingerprint: string().min(1),
	path: string().min(1),
	reasonCode: nullFactory(),
	reasonText: nullFactory(),
	runtimeContext: providerDispatchRuntimeContextSchema,
	status: literal("ready"),
	workspaceMode: profileWorkspaceModeSchema
});
const openMeldDaemonConversationExecutionRuntimeWorkspaceMissingSchema = strictObject({
	configPath: string().min(1),
	configuredWorkspacePath: nullFactory(),
	fingerprint: nullFactory(),
	path: nullFactory(),
	reasonCode: literal("workspace.missing"),
	reasonText: string().min(1),
	runtimeContext: nullFactory(),
	status: literal("missing"),
	workspaceMode: nullFactory()
});
const openMeldDaemonConversationExecutionRuntimeWorkspaceInvalidSchema = strictObject({
	configPath: string().min(1),
	configuredWorkspacePath: union([string().min(1), nullFactory()]),
	fingerprint: nullFactory(),
	path: nullFactory(),
	reasonCode: literal("workspace.invalid"),
	reasonText: string().min(1),
	runtimeContext: nullFactory(),
	status: literal("invalid"),
	workspaceMode: union([profileWorkspaceModeSchema, nullFactory()])
});
const openMeldDaemonConversationExecutionRuntimeWorkspaceSchema = discriminatedUnion("status", [
	openMeldDaemonConversationExecutionRuntimeWorkspaceReadySchema,
	openMeldDaemonConversationExecutionRuntimeWorkspaceMissingSchema,
	openMeldDaemonConversationExecutionRuntimeWorkspaceInvalidSchema
]);
const openMeldDaemonConversationExecutionLocalRuntimeContextStatusSchema = enumFactory([
	"missing",
	"mismatch",
	"ready"
]);
const openMeldDaemonProviderConversationProofStatusSchema = enumFactory([
	"failed",
	"not_applicable",
	"not_run",
	"passed"
]);
const openMeldDaemonProviderConversationProofProviderSchema = enumFactory([
	"codex",
	"claude-code",
	"openclaw"
]);
const openMeldDaemonProviderConversationProofMethodSchema = enumFactory(["codex_app_server_thread_resume"]);
const openMeldDaemonProviderConversationProofSchema = strictObject({
	agentControllerConversationId: nullableNonEmptyStringSchema,
	checkedAt: union([iso.datetime({ offset: true }), nullFactory()]),
	errorCode: nullableNonEmptyStringSchema,
	errorMessage: nullableNonEmptyStringSchema,
	errorType: nullableNonEmptyStringSchema,
	expiresAt: union([iso.datetime({ offset: true }), nullFactory()]),
	intrusive: literal(false),
	method: union([openMeldDaemonProviderConversationProofMethodSchema, nullFactory()]),
	provider: union([openMeldDaemonProviderConversationProofProviderSchema, nullFactory()]),
	status: openMeldDaemonProviderConversationProofStatusSchema,
	transportMode: union([runtimeTransportModeSchema, nullFactory()])
});
const openMeldDaemonConversationExecutionStatusPayloadSchema = strictObject({
	agentContextLookupKey: string().min(1),
	controllerLaunchStatus: openMeldDaemonConversationExecutionControllerLaunchStatusSchema,
	executionState: conversationExecutionStateSchema.nullable(),
	localRuntimeContext: strictObject({
		agentContextLookupKey: string().min(1),
		runtimeWorkspaceFingerprint: nullableNonEmptyStringSchema,
		status: openMeldDaemonConversationExecutionLocalRuntimeContextStatusSchema
	}),
	providerConversationProof: openMeldDaemonProviderConversationProofSchema,
	runtimeWorkspace: openMeldDaemonConversationExecutionRuntimeWorkspaceSchema
});
const openMeldDaemonSyncRouteCatalogStatusSchema = enumFactory([
	"changed",
	"registered",
	"unchanged",
	"failed",
	"deferred"
]);
const DAEMON_PROFILE_SKIP_REASON_VALUES = [
	"projection.missing",
	"controller.registry.disabled",
	"controller.launch.needs_repair",
	"runtime.adapter.unsupported",
	"controller.binding.disabled",
	"controller.binding.incomplete",
	"profile.status.inactive",
	"profile.binding.read_unavailable",
	"profile.binding.unbound",
	"workspace.missing",
	"workspace.invalid"
];
const daemonProfileSkipReasonSchema = enumFactory(DAEMON_PROFILE_SKIP_REASON_VALUES);
const skippedDaemonProfileSchema = strictObject({
	profileId: agentProfileIdSchema,
	profileName: nullableNonEmptyStringSchema,
	skipReason: daemonProfileSkipReasonSchema
});
const openMeldDaemonSyncRouteCatalogPayloadSchema = strictObject({
	accepted: boolean(),
	syncStatus: openMeldDaemonSyncRouteCatalogStatusSchema,
	profileIds: array(agentProfileIdSchema),
	addedProfileIds: array(agentProfileIdSchema).optional(),
	keptProfileIds: array(agentProfileIdSchema).optional(),
	removedProfileIds: array(agentProfileIdSchema).optional(),
	skippedProfiles: array(skippedDaemonProfileSchema).optional(),
	note: string().min(1).optional(),
	errorMessage: string().min(1).optional()
});
const openMeldDaemonRequestAgentActivitySyncPayloadSchema = strictObject({ accepted: boolean() });
const openMeldDaemonSetLocalAgentEnabledPayloadSchema = strictObject({
	accepted: boolean(),
	agentControllerRef: string().min(1).optional(),
	agentId: string().trim().min(1).max(256),
	changed: boolean(),
	enabled: boolean(),
	message: string().min(1).optional(),
	reasonCode: string().min(1).optional(),
	reportSyncStatus: enumFactory([
		"submitted",
		"skipped",
		"failed"
	]),
	status: enumFactory([
		"updated",
		"unchanged",
		"rejected"
	])
});
const openMeldDaemonManageLocalAgentInstallationPayloadSchema = discriminatedUnion("accepted", [strictObject({
	accepted: literal(true),
	agentControllerRef: string().min(1),
	agentId: string().trim().min(1).max(256),
	changed: boolean(),
	installations: array(localAgentInstallationOptionSchema),
	reportSyncStatus: enumFactory([
		"submitted",
		"skipped",
		"failed"
	]),
	selectionStatus: enumFactory([
		"available",
		"blocked_by_work",
		"operation_in_progress"
	]).optional(),
	selectedInstallationId: string().trim().min(1).max(256).nullable(),
	status: enumFactory([
		"listed",
		"updated",
		"unchanged"
	])
}), strictObject({
	accepted: literal(false),
	agentId: string().trim().min(1).max(256),
	changed: literal(false),
	installations: array(localAgentInstallationOptionSchema),
	message: string().min(1).optional(),
	reasonCode: string().min(1).optional(),
	reportSyncStatus: literal("failed"),
	selectionStatus: enumFactory([
		"available",
		"blocked_by_work",
		"operation_in_progress"
	]).optional(),
	selectedInstallationId: string().trim().min(1).max(256).nullable(),
	status: literal("rejected")
})]);
const openMeldDaemonSetProfileWorkspacePayloadSchema = discriminatedUnion("accepted", [strictObject({
	accepted: literal(true),
	targetProfileId: agentProfileIdSchema,
	status: literal("updated"),
	workspaceMode: profileWorkspaceModeSchema,
	workspacePath: string().min(1)
}), strictObject({
	accepted: literal(false),
	targetProfileId: agentProfileIdSchema,
	message: string().min(1).optional(),
	reasonCode: string().min(1).optional(),
	status: literal("rejected")
})]);
const openMeldDaemonControlPlaneRequestBaseSchema = strictObject({
	v: literal(1),
	requestId: string().min(1).optional(),
	authToken: string().min(1).optional()
});
const openMeldDaemonControlPlaneRequestSchema = discriminatedUnion("command", [
	openMeldDaemonControlPlaneRequestBaseSchema.extend({ command: literal("account_control_capabilities") }),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({ command: literal("status") }),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({ command: literal("health") }),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({ command: literal("history") }),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({
		command: literal("interrupt"),
		payload: openMeldDaemonInterruptRequestPayloadSchema
	}),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({
		command: literal("cancel"),
		payload: openMeldDaemonCancelRequestPayloadSchema
	}),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({
		command: literal("conversation_execution_status"),
		payload: openMeldDaemonConversationExecutionStatusRequestPayloadSchema
	}),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({
		command: literal("sync_route_catalog"),
		payload: openMeldDaemonSyncRouteCatalogRequestPayloadSchema
	}),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({
		command: literal("request_agent_activity_sync"),
		payload: openMeldDaemonRequestAgentActivitySyncRequestPayloadSchema
	}),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({
		command: literal("set_local_agent_enabled"),
		payload: openMeldDaemonSetLocalAgentEnabledRequestPayloadSchema
	}),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({
		command: literal("set_profile_workspace"),
		payload: openMeldDaemonSetProfileWorkspaceRequestPayloadSchema
	}),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({ command: literal("service_update_readiness") }),
	openMeldDaemonControlPlaneRequestBaseSchema.extend({
		command: literal("shutdown"),
		payload: openMeldDaemonShutdownRequestPayloadSchema.optional()
	})
]);
const openMeldDaemonStatusPayloadSchema = strictObject({
	installed: boolean(),
	running: boolean(),
	daemonVersion: string().min(1),
	runtimeRoot: string().min(1),
	checkedAt: string().min(1)
});
const openMeldDaemonHealthPayloadSchema = strictObject({
	state: openMeldDaemonHealthStateSchema,
	runtimeState: openMeldDaemonRuntimeHealthStateSchema.optional(),
	reasonCode: string().min(1),
	checkedAt: string().min(1)
});
const openMeldDaemonHistoryEntrySchema = strictObject({
	id: string().min(1),
	ts: string().min(1),
	code: string().min(1),
	message: string().min(1)
});
const openMeldDaemonHistoryPayloadSchema = strictObject({
	entries: array(openMeldDaemonHistoryEntrySchema),
	nextCursor: string().min(1).optional()
});
const openMeldDaemonInterruptPayloadSchema = strictObject({
	accepted: boolean(),
	target: openMeldDaemonControlTargetSelectorSchema,
	resolvedDispatchId: string().min(1).optional(),
	resolvedRuntimeCoordinatorExecutionId: string().min(1).optional(),
	resolvedTaskId: string().min(1).optional(),
	note: string().min(1).optional(),
	reasonCode: string().min(1).optional()
});
/**
* What accepting a cancellation established, carried on `reasonCode`.
*
* `accepted` alone only means the control request was taken: an adapter can
* register a stop signal before the provider turn is acknowledged, and
* interrupting a started turn is a request, not a completion barrier. Work that
* replaces the attempt needs `adapter.cancel.withdrawn_before_start`.
*
* This rides the existing free-form reason code on purpose. These payloads are
* strict objects, so a Service that emitted a new key would have its whole
* frame rejected by a Core that predates it, and local components can reach a
* user before the matching Core deploy.
*/
const OPENMELD_DAEMON_CANCEL_WITHDRAWN_BEFORE_START_REASON_CODE = "adapter.cancel.withdrawn_before_start";
const OPENMELD_DAEMON_CANCEL_STOP_REQUESTED_AFTER_START_REASON_CODE = "adapter.cancel.stop_requested_after_start";
const openMeldDaemonCancelPayloadSchema = strictObject({
	accepted: boolean(),
	target: openMeldDaemonControlTargetSelectorSchema,
	resolvedDispatchId: string().min(1).optional(),
	resolvedRuntimeCoordinatorExecutionId: string().min(1).optional(),
	resolvedTaskId: string().min(1).optional(),
	note: string().min(1).optional(),
	reasonCode: string().min(1).optional()
});
const openMeldDaemonControlPlaneErrorPayloadSchema = strictObject({
	errorCode: string().min(1),
	errorMessage: string().min(1),
	note: string().min(1).optional()
});
const openMeldDaemonShutdownPayloadSchema = strictObject({
	accepted: boolean(),
	mode: enumFactory(["graceful", "force"]),
	gracePeriodMs: number().int().nonnegative()
});
const openMeldDaemonControlPlaneResponseBaseSchema = strictObject({
	v: literal(1),
	requestId: string().min(1).optional()
});
const openMeldDaemonStatusResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.status"),
	payload: openMeldDaemonStatusPayloadSchema
});
const openMeldDaemonHealthResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.health"),
	payload: openMeldDaemonHealthPayloadSchema
});
const openMeldDaemonHistoryResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.history"),
	payload: openMeldDaemonHistoryPayloadSchema
});
const openMeldDaemonInterruptResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.interrupt"),
	payload: openMeldDaemonInterruptPayloadSchema
});
const openMeldDaemonCancelResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.cancel"),
	payload: openMeldDaemonCancelPayloadSchema
});
const openMeldDaemonConversationExecutionStatusResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.conversation_execution_status"),
	payload: openMeldDaemonConversationExecutionStatusPayloadSchema
});
const openMeldDaemonSyncRouteCatalogResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.sync_route_catalog"),
	payload: openMeldDaemonSyncRouteCatalogPayloadSchema
});
const openMeldDaemonRequestAgentActivitySyncResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.agent_activity_sync_requested"),
	payload: openMeldDaemonRequestAgentActivitySyncPayloadSchema
});
const openMeldDaemonSetLocalAgentEnabledResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.set_local_agent_enabled"),
	payload: openMeldDaemonSetLocalAgentEnabledPayloadSchema
});
const openMeldDaemonSetProfileWorkspaceResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.set_profile_workspace"),
	payload: openMeldDaemonSetProfileWorkspacePayloadSchema
});
const openMeldDaemonShutdownResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.shutdown"),
	payload: openMeldDaemonShutdownPayloadSchema
});
const openMeldDaemonServiceUpdateReadinessResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.update_readiness"),
	payload: openMeldDaemonServiceUpdateReadinessPayloadSchema
});
const openMeldDaemonControlPlaneErrorResponseSchema = openMeldDaemonControlPlaneResponseBaseSchema.extend({
	code: literal("service.error"),
	payload: openMeldDaemonControlPlaneErrorPayloadSchema
});
const openMeldDaemonControlPlaneResponseSchema = discriminatedUnion("code", [
	openMeldDaemonControlPlaneResponseBaseSchema.extend({
		code: literal("service.account_control_capabilities"),
		payload: strictObject({ supported: boolean() })
	}),
	openMeldDaemonStatusResponseSchema,
	openMeldDaemonHealthResponseSchema,
	openMeldDaemonHistoryResponseSchema,
	openMeldDaemonInterruptResponseSchema,
	openMeldDaemonCancelResponseSchema,
	openMeldDaemonConversationExecutionStatusResponseSchema,
	openMeldDaemonSyncRouteCatalogResponseSchema,
	openMeldDaemonRequestAgentActivitySyncResponseSchema,
	openMeldDaemonSetLocalAgentEnabledResponseSchema,
	openMeldDaemonSetProfileWorkspaceResponseSchema,
	openMeldDaemonServiceUpdateReadinessResponseSchema,
	openMeldDaemonShutdownResponseSchema,
	openMeldDaemonControlPlaneErrorResponseSchema
]);
//#endregion
export { DAEMON_PROFILE_SKIP_REASON_VALUES, OPENMELD_DAEMON_CANCEL_STOP_REQUESTED_AFTER_START_REASON_CODE, OPENMELD_DAEMON_CANCEL_WITHDRAWN_BEFORE_START_REASON_CODE, OPENMELD_DAEMON_CONTROL_PLANE_SCHEMA_VERSION, daemonProfileSkipReasonSchema, localAgentInstallationOptionSchema, openMeldDaemonCancelPayloadSchema, openMeldDaemonCancelRequestPayloadSchema, openMeldDaemonCancelResponseSchema, openMeldDaemonControlPlaneCommandSchema, openMeldDaemonControlPlaneErrorPayloadSchema, openMeldDaemonControlPlaneErrorResponseSchema, openMeldDaemonControlPlaneRequestSchema, openMeldDaemonControlPlaneResponseSchema, openMeldDaemonControlTargetSelectorSchema, openMeldDaemonConversationExecutionControllerLaunchStatusSchema, openMeldDaemonConversationExecutionLocalRuntimeContextStatusSchema, openMeldDaemonConversationExecutionRuntimeWorkspaceInvalidSchema, openMeldDaemonConversationExecutionRuntimeWorkspaceMissingSchema, openMeldDaemonConversationExecutionRuntimeWorkspaceReadySchema, openMeldDaemonConversationExecutionRuntimeWorkspaceSchema, openMeldDaemonConversationExecutionStatusPayloadSchema, openMeldDaemonConversationExecutionStatusRequestPayloadSchema, openMeldDaemonConversationExecutionStatusResponseSchema, openMeldDaemonHealthPayloadSchema, openMeldDaemonHealthResponseSchema, openMeldDaemonHealthStateSchema, openMeldDaemonHistoryEntrySchema, openMeldDaemonHistoryPayloadSchema, openMeldDaemonHistoryResponseSchema, openMeldDaemonInterruptPayloadSchema, openMeldDaemonInterruptRequestPayloadSchema, openMeldDaemonInterruptResponseSchema, openMeldDaemonManageLocalAgentInstallationPayloadSchema, openMeldDaemonManageLocalAgentInstallationRequestPayloadSchema, openMeldDaemonProviderConversationProofMethodSchema, openMeldDaemonProviderConversationProofProviderSchema, openMeldDaemonProviderConversationProofSchema, openMeldDaemonProviderConversationProofStatusSchema, openMeldDaemonRequestAgentActivitySyncPayloadSchema, openMeldDaemonRequestAgentActivitySyncRequestPayloadSchema, openMeldDaemonRequestAgentActivitySyncResponseSchema, openMeldDaemonRuntimeHealthStateSchema, openMeldDaemonServiceUpdateReadinessPayloadSchema, openMeldDaemonServiceUpdateReadinessResponseSchema, openMeldDaemonSetLocalAgentEnabledPayloadSchema, openMeldDaemonSetLocalAgentEnabledRequestPayloadSchema, openMeldDaemonSetLocalAgentEnabledResponseSchema, openMeldDaemonSetProfileWorkspacePayloadSchema, openMeldDaemonSetProfileWorkspaceRequestPayloadSchema, openMeldDaemonSetProfileWorkspaceResponseSchema, openMeldDaemonShutdownPayloadSchema, openMeldDaemonShutdownRequestPayloadSchema, openMeldDaemonShutdownResponseSchema, openMeldDaemonStatusPayloadSchema, openMeldDaemonStatusResponseSchema, openMeldDaemonSyncRouteCatalogPayloadSchema, openMeldDaemonSyncRouteCatalogRequestPayloadSchema, openMeldDaemonSyncRouteCatalogResponseSchema, openMeldDaemonSyncRouteCatalogStatusSchema, skippedDaemonProfileSchema };

//# sourceMappingURL=control-plane.js.map